import { IAvatarPromptStyle } from "@next-platform/packages-ts-types";
import { Icon, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useTranslation } from "@next-platform/client-common-core";
import { useCallback, useMemo, useState } from "react";

import type { IPromptImageStyleSelectorProps } from "@/components/AvatarGeneratorModal/types";
import { SelectionModal } from "@/components/SelectionModal";
import Trans from "@/components/Trans/Trans";

const STYLE_SELECTOR_GRID_COLS = { mobile: 1, desktop: 2 } as const;

// Renders `t(style.i18nKey, { defaultValue: style.name })` when an i18n key is provided,
// otherwise falls back to the raw name so Contentful authors can add styles without a
// translation pass.
// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
const labelForStyle = (style: IAvatarPromptStyle, t: (key: string, options?: { defaultValue?: string }) => string): string =>
  style.i18nKey ? t(style.i18nKey, { defaultValue: style.name }) : style.name;

export const PromptImageStyleSelector = ({ promptImageStyle, promptImageStyles, onStyleChange }: IPromptImageStyleSelectorProps) => {
  const { t } = useTranslation();

  const [stateIsModalOpen, setStateIsModalOpen] = useState(false);
  const handleOpenModal = useCallback(() => setStateIsModalOpen(true), []);

  const items = useMemo(
    () =>
      promptImageStyles.map((style: IAvatarPromptStyle) => ({
        value: style.id,
        label: labelForStyle(style, t),
        image: style.imageUrl,
      })),
    [promptImageStyles, t],
  );

  const selectedStyle = promptImageStyles.find((style: IAvatarPromptStyle) => style.id === promptImageStyle);

  return (
    <>
      <button
        onClick={handleOpenModal}
        className="w-full flex items-center justify-between py-3 px-0 bg-transparent border-0 text-left cursor-pointer hover:opacity-80 transition-opacity"
      >
        <div className="flex flex-col flex-1 min-w-0">
          <Text variant={TextVariants.LABEL_LARGE_REGULAR} className="ml-[2px]">
            <Trans i18nKey="common:modal.generateAvatar.promptStyles.label" />
          </Text>
          <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11 pl-1">
            <Trans i18nKey="common:modal.generateAvatar.promptStyles.helperText" />
          </Text>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
          {selectedStyle?.imageUrl ? (
            <img src={selectedStyle.imageUrl} alt={labelForStyle(selectedStyle, t)} className="w-8 h-8 rounded object-cover" />
          ) : null}
          <Text variant={TextVariants.LABEL_MEDIUM_REGULAR}>
            {selectedStyle ? labelForStyle(selectedStyle, t) : <Trans i18nKey="common:modal.generateAvatar.promptStyles.selectTitle" />}
          </Text>
          <Icon icon={IconVariants.CHEVRON_RIGHT} />
        </div>
      </button>

      <SelectionModal<string>
        open={stateIsModalOpen}
        onOpenChange={setStateIsModalOpen}
        modalTitle={t("common:modal.generateAvatar.promptStyles.selectTitle")}
        selectedValue={promptImageStyle}
        items={items}
        onSelectionChange={onStyleChange}
        gridCols={STYLE_SELECTOR_GRID_COLS}
      />
    </>
  );
};
